import { Grid } from '@mui/material'
import FormControlRadio, { radioButtonChoiceOptions } from 'components/FormControlRadio'
import { default as FormInput, default as InputMultiline } from 'components/FormInput'
import InputDatePicker from 'components/FormInputDatePicker'
import dayjs from 'dayjs'
import React, { useCallback, useEffect } from 'react'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'
import {
  hearingCircumstancesInit,
  hearingCircumstancesScheme,
  HearingCircumstancesType,
} from 'types/hearing-circumstances.type'
import { AddressGroup } from './components/AddressGroup'
import circumstancesService from '../../../../services/circumstances.service'
import { useOutletContext, useParams } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { useBackdrop } from 'contexts/backdrop.context'
import { useSnackBar } from 'contexts/snackbar.context'
import { SystemMessages } from 'utils/enums/system-message.enum'
import { HttpErrorType } from 'types/http.type'
import { errorColor, errorMessage } from 'utils/error.util'
import { OutletContextType } from 'pages/CreateHearing'
import { StepIndex } from 'pages/CreateHearing/enums/step.enum'

export const StepCircumstancesPage: React.FC = () => {
  const { id } = useParams()
  const { showSnackBar } = useSnackBar()
  const { handleBackdropOpen, handleBackdropClose } = useBackdrop()
  const { formRefs } = useOutletContext<OutletContextType>()

  const form = useForm<HearingCircumstancesType>({
    resolver: zodResolver(hearingCircumstancesScheme),
    defaultValues: hearingCircumstancesInit,
  })

  const {
    control,
    watch,
    setValue,
    reset,
    handleSubmit,
    clearErrors,
    setError,
    formState: { isDirty, dirtyFields },
  } = form

  const hasBadTreatmentsValue = watch('notConcretaTortura')
  const hasInjuriesValue = watch('vestLesaoCorp')

  const hasDirtyFields = Object.keys(dirtyFields).length > 0 && dirtyFields.constructor === Object

  const getCircumstances = useCallback(async () => {
    try {
      await circumstancesService.getById(id).then((response) => {
        const initialValues = response.data ? response.data : hearingCircumstancesInit
        reset(initialValues)
      })
    } catch (err) {
      console.error('Circumstances Request: ', err)
    }
  }, [id])

  useEffect(() => {
    getCircumstances()
  }, [getCircumstances])

  const saveCircumstances = async (data: HearingCircumstancesType): Promise<void> => {
    if (id) {
      handleBackdropOpen()
      await circumstancesService
        .save(id, data)
        .then(async () => {
          showSnackBar(SystemMessages.MESSAGE_001, 'success')
          await getCircumstances()
        })
        .catch((error: HttpErrorType) => {
          showSnackBar(errorMessage(error), errorColor(error))
        })
        .finally(() => handleBackdropClose())
    }
  }

  const onError = (errors: FieldErrors<HearingCircumstancesType>) => {
    clearErrors()
    const recursiveSetError = (errors: FieldErrors<HearingCircumstancesType>, parentPath = '') => {
      Object.entries(errors).forEach(([key, error]: any) => {
        const path = parentPath ? `${parentPath}.${key}` : key

        if (error?.ref) {
          // Se houver uma referência (ref), usamos o nome da referência para definir o erro
          setError(error.ref.name, { type: error.type, message: error.message })
        } else if (typeof error === 'object') {
          // Recursivamente percorre os objetos internos
          recursiveSetError(error, path)
        }
      })
    }
    recursiveSetError(errors)
    console.log('onError', errors)
    return Promise.reject(errors)
  }

  formRefs.current[StepIndex.CIRCUNSTANCIAS] = {
    submit: async () => {
      if (isDirty && hasDirtyFields) {
        await handleSubmit(saveCircumstances, onError)()
      }
      return Promise.resolve()
    },
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(saveCircumstances)}>
        <Grid container spacing={2} rowSpacing={4} sx={{ mb: 4 }}>
          <Grid item md={4} xs={12}>
            <InputDatePicker
              control={control}
              name='dtFato'
              label='Data do fato'
              minDate={dayjs('2020-01-01')}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <InputDatePicker
              control={control}
              name='dtApreensao'
              label='Data da apreensão'
              minDate={dayjs('2020-01-01')}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <FormInput
              control={control}
              name='localPernoite'
              label='Local do pernoite'
              placeholder='Informe o local do pernoite'
              maxLength={60}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 4, mb: 4 }}>
          <AddressGroup control={control} watch={watch} setValue={setValue} />
        </Grid>

        <Grid container spacing={2} rowSpacing={4} sx={{ mb: 1 }}>
          <Grid item xs={12}>
            <FormControlRadio
              control={control}
              name='notConcretaTortura'
              label='Há notícias concretas ou fundadas da prática de tortura ou maus tratos no momento ou após a apreensão?'
              options={radioButtonChoiceOptions}
            />
          </Grid>
          {hasBadTreatmentsValue === 'S' && (
            <>
              <Grid item xs={12}>
                <InputMultiline
                  control={control}
                  name='breveRelatoNoticia'
                  label='Breve relato da notícia'
                  placeholder='Informe o breve relato da notícia'
                  maxLength={210}
                  multiline
                  minRows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlRadio
                  control={control}
                  name='vestLesaoCorp'
                  label='Há vestígios de lesões corporais que indiciem prática de tortura física ou maus tratos?'
                  options={radioButtonChoiceOptions}
                />
              </Grid>
              {hasInjuriesValue === 'S' && (
                <>
                  <Grid item xs={12}>
                    <InputMultiline
                      control={control}
                      name='brevDescVestigio'
                      label='Breve descrição dos vestígios'
                      placeholder='Informe o breve descrição dos vestígios'
                      maxLength={210}
                      multiline
                      minRows={3}
                      required={hasInjuriesValue === 'S'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputMultiline
                      control={control}
                      name='providenciaAdotada'
                      label='Indique a providência que será adotada'
                      placeholder='Informe a providência que será adotada'
                      maxLength={210}
                      multiline
                      minRows={3}
                    />
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </form>
    </FormProvider>
  )
}
